import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

import type { Database } from "@/integrations/supabase/types";
import { normalizePhone } from "@/lib/phone";

export type VolunteerTrack = "remote" | "onsite" | "both";

export type ApplicationInput = {
  full_name: string;
  email: string;
  phone?: string;
  birthday?: string;
  city?: string;
  state?: string;
  why_help: string;
  t1d_connection?: string;
  programs_of_interest: string[];
  availability?: string;
  volunteer_track?: string;
  sms_consent?: boolean;
  sms_consent_source?: string;
  email_consent?: boolean;
  email_consent_source?: string;
  /** volunteer_profiles.id of the volunteer who recruited this applicant. */
  referral_code?: string;
  /** Athlete referral code (?ref=, 8-16 letters/numbers). */
  athlete_ref?: string | null;
};

export const listPrograms = createServerFn({ method: "GET" }).handler(async () => {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const client = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });

  const { data, error } = await client
    .from("programs")
    .select("id, slug, name, summary, site_url, sort_order")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
});

export const submitApplication = createServerFn({ method: "POST" })
  .inputValidator((data: ApplicationInput) => data)
  .handler(async ({ data }) => {
    const name = data.full_name?.trim() ?? "";
    const email = data.email?.trim().toLowerCase() ?? "";
    if (name.length < 2 || name.length > 120) throw new Error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 255) {
      throw new Error("Please enter a valid email address.");
    }
    if ((data.why_help ?? "").trim().length < 10) {
      throw new Error("Tell us a little more about why you want to help.");
    }
    const track: VolunteerTrack = (["remote", "onsite", "both"] as const).includes(
      data.volunteer_track as VolunteerTrack,
    )
      ? (data.volunteer_track as VolunteerTrack)
      : "remote";

    if (!(data.city ?? "").trim()) throw new Error("Please enter your city.");
    if (!(data.state ?? "").trim()) throw new Error("Please select your state.");
    if (!(data.availability ?? "").trim()) throw new Error("Please tell us your availability.");
    if (!(data.t1d_connection ?? "").trim()) {
      throw new Error("Please tell us your relationship to Type 1 Diabetes.");
    }
    if ((data.programs_of_interest ?? []).length === 0) {
      throw new Error("Please select at least one program you would like to help with.");
    }

    const url = process.env.SUPABASE_URL!;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const client = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    const phone = normalizePhone(data.phone);
    if (!phone.e164) throw new Error("Please enter a phone number.");
    if (phone.error) throw new Error(phone.error);

    // SMS consent only counts when there is a number to text.
    const smsConsent = Boolean(data.sms_consent) && Boolean(phone.e164);
    if (Boolean(data.sms_consent) && !phone.e164) {
      throw new Error("Add a mobile number to opt in to text messages.");
    }

    const birthday = (data.birthday ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
      throw new Error("Please enter a valid birthday.");
    }

    const recordId = crypto.randomUUID();
    const { normalizeAthleteRef } = await import("@/lib/athlete-ref");
    const athleteRef = normalizeAthleteRef(data.athlete_ref);
    const { error } = await client.from("volunteer_applications").insert({
      id: recordId,
      athlete_ref: athleteRef,
      full_name: name,
      email,
      phone: phone.e164,
      birthday,
      city: data.city?.trim().slice(0, 120) || null,
      state: data.state?.trim().slice(0, 60) || null,
      why_help: data.why_help.trim().slice(0, 2000),
      t1d_connection: data.t1d_connection?.trim().slice(0, 1000) || null,
      programs_of_interest: data.programs_of_interest.slice(0, 12),
      availability: data.availability?.trim().slice(0, 500) || null,
      volunteer_track: track,
      sms_consent: smsConsent,
      sms_consent_at: smsConsent ? new Date().toISOString() : null,
      sms_consent_source: smsConsent
        ? (data.sms_consent_source?.trim().slice(0, 120) || "public application form")
        : null,
      email_consent: Boolean(data.email_consent),
      email_consent_at: data.email_consent ? new Date().toISOString() : null,
      email_consent_source: data.email_consent
        ? (data.email_consent_source?.trim().slice(0, 120) || "public application form")
        : null,
      referral_code: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(String(data.referral_code ?? "").trim())
        ? String(data.referral_code).trim().toLowerCase()
        : null,
      status: "pending",
    });

    if (error) throw new Error(error.message);

    try {
      const { reportSignupInBackground } = await import("@/lib/athlete-referral.server");
      reportSignupInBackground(recordId, athleteRef, "email");
    } catch {
      // Referral reporting must never break sign-up.
    }

    // They said yes, so pull them out of the invite funnel.
    try {
      const { markConvertedByEmail } = await import("@/lib/volunteer-campaign.server");
      await markConvertedByEmail(email);
    } catch {
      // markConvertedByEmail already reports its own failures.
    }



    // Best effort internal alert; a delivery problem must not fail the application.
    try {
      const { notifyByKey } = await import("@/lib/notifications.server");
      const { resolveOrigin } = await import("@/lib/events.server");
      const origin = resolveOrigin((data as { origin?: string }).origin);
      await notifyByKey("application_submitted", "application-submitted", {
        applicantName: name,
        applicantEmail: email,
        applicantPhone: phone.e164,
        track,
        city: data.city?.trim().slice(0, 120) || null,
        state: data.state?.trim().slice(0, 60) || null,
        t1dConnection: data.t1d_connection?.trim().slice(0, 1000) || null,
        availability: data.availability?.trim().slice(0, 500) || null,
        whyHelp: data.why_help.trim().slice(0, 2000),
        programs: (data.programs_of_interest ?? []).slice(0, 12),
        adminUrl: `${origin}/t1dwpanel/applications`,
      });
    } catch {
      // notifyByKey already reports its own failures.
    }

    return { ok: true };
  });
